import classNames from 'classnames'
import { memo, ReactNode, useCallback, useLayoutEffect, useRef } from 'react'

import { debounce, TableCell, TableCellProps } from '@mui/material'

import type { GRTableColumn } from '../GRTable'
import { SortOrder } from '../types/SortOrder'
import './HeaderCell.scss'
import { SortLabel } from './SortLabel/SortLabel'

/**
 * Component representing header cells of a table
 */

type HeaderCellProps<RowType, CustomPropsType, ColumnIdType = void> = {
  column?: GRTableColumn<RowType, CustomPropsType, ColumnIdType>
  onHeaderClick?: (columnIndex: number) => void
  onMeasure?: (colWidth: number, columnIndex: number) => void
  cellProps?: TableCellProps
  sortable?: boolean | 'manual'
  sortOrder?: SortOrder
  defaultSortOrder?: SortOrder
  grouping?: boolean
  columnIndex: number
  className?: string
  children?: ReactNode
}

const Component = <RowType extends object, CustomPropsType extends object | undefined, ColumnIdType extends string | void>({
  onHeaderClick,
  onMeasure,
  cellProps,
  sortable,
  sortOrder,
  defaultSortOrder,
  grouping,
  columnIndex,
  className,
  children,
}: HeaderCellProps<RowType, CustomPropsType, ColumnIdType>) => {
  const cellRef = useRef<HTMLTableCellElement>(null)

  const isColumnSortingActive = !!sortOrder
  const cellClasses = classNames('HeaderCell', className, { 'HeaderCell--grouping': grouping })

  const handleMeasure = useCallback(() => {
    if (onMeasure) {
      onMeasure(cellRef?.current?.getBoundingClientRect().width || 0, columnIndex)
    }
  }, [onMeasure, cellRef, columnIndex])

  useLayoutEffect(() => {
    handleMeasure()
  }, [handleMeasure])

  const handleHeaderClick = useCallback(() => {
    if (onHeaderClick) {
      onHeaderClick(columnIndex)
    }
  }, [onHeaderClick, columnIndex])

  useLayoutEffect(() => {
    const element = cellRef.current
    const debouncedMeasure = debounce(() => {
      handleMeasure()
    }, 300)
    const resizeObserver = new ResizeObserver(debouncedMeasure)
    resizeObserver.observe(element as Element)

    return () => {
      resizeObserver.disconnect()
      debouncedMeasure.clear()
    }
  }, [handleMeasure])

  return (
    <TableCell variant="head" align="center" sx={{ px: 0, py: 2 }} {...cellProps} className={cellClasses} ref={cellRef}>
      {sortable && sortable !== 'manual' ? (
        <SortLabel active={isColumnSortingActive} direction={sortOrder || defaultSortOrder} onClick={handleHeaderClick}>
          {children}
        </SortLabel>
      ) : (
        <>{children}</>
      )}
    </TableCell>
  )
}

export const HeaderCell = memo(Component) as typeof Component
